import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type {
  EstatusSolicitud, Pagina, PartidaSolicitud, Refaccion, SolicitudAlmacen,
  SolicitudEnBandeja,
} from '../api/tipos';
import { Cargando, Dialogo, Error, Faltantes, Paginacion } from '../componentes/comunes';
import { BuscadorRefaccion } from '../componentes/BuscadorRefaccion';
import { useSesion } from '../sesion';

/**
 * Lo que las órdenes le piden al almacén.
 *
 * Almacén no teclea la disponibilidad: al abrir la solicitud cada pieza
 * ya viene marcada y con el desglose de en qué bodega está. Lo que le
 * toca es decidir —surtir o escalar a compras—, que es donde su criterio
 * aporta algo.
 *
 * Escalar es explícito y no automático: a veces se resuelve con un
 * traspaso entre bodegas o con una pieza equivalente, y comprar sin que
 * nadie lo decida es como se acaban comprando cosas que estaban en el
 * anaquel de al lado.
 */

const ETIQUETAS: Record<string, { texto: string; clase: string }> = {
  en_revision: { texto: 'En revisión', clase: '' },
  disponible: { texto: 'Hay existencia', clase: 'si' },
  parcial: { texto: 'Surtida a medias', clase: '' },
  sin_stock: { texto: 'Sin existencia', clase: 'no' },
  surtida: { texto: 'Surtida', clase: 'si' },
  cancelada: { texto: 'Cancelada', clase: 'no' },
};

/** En qué va la solicitud de compra que atiende la pieza. */
const ETIQUETAS_COMPRA: Record<string, string> = {
  creada: 'Pendiente en compras',
  en_oc: 'En orden de compra',
  atendida: 'Atendida',
  cancelada: 'Cancelada',
};

export function Etiqueta({ valor }: { valor: string }) {
  const e = ETIQUETAS[valor] ?? { texto: valor, clase: '' };
  return <span className={`etiqueta ${e.clase}`}>{e.texto}</span>;
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function SolicitudesAlmacen() {
  const [situacion, setSituacion] = useState('pendientes');
  const [busqueda, setBusqueda] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<SolicitudEnBandeja> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [abierta, setAbierta] = useState<string | null>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<SolicitudEnBandeja>>('/solicitudes-almacen', {
      situacion: situacion || undefined,
      busqueda: busqueda || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [situacion, busqueda, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <>
      <div className="panel">
        <div className="controles">
          <select
            value={situacion}
            onChange={(e) => { setSituacion(e.target.value); setNumero(1); }}
          >
            <option value="pendientes">Pendientes</option>
            <option value="disponible">Con existencia</option>
            <option value="sin_stock">Sin existencia</option>
            <option value="parcial">Surtidas a medias</option>
            <option value="surtida">Surtidas</option>
            <option value="cancelada">Canceladas</option>
            <option value="todas">Todas</option>
          </select>
          <input
            type="search"
            placeholder="Folio de la solicitud o de la orden…"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
          />
        </div>

        <Error error={error} />

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">No hay solicitudes con ese filtro.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Orden</th>
                    <th>Sucursal</th>
                    <th>Pedida</th>
                    <th className="numero">Piezas</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((s) => (
                    <tr key={s.id}>
                      <td className="mono">
                        <button className="enlace" onClick={() => setAbierta(s.id)}>
                          {s.folio}
                        </button>
                      </td>
                      {/* Una anticipada no tiene orden todavía: se pidió
                          para la visita que viene y espera a que la OT
                          llegue. Se marca porque almacén puede revisarla
                          y escalarla, pero no surtirla. */}
                      <td className="mono">
                        {s.orden
                          ? s.orden.folio
                          : <span className="etiqueta">anticipada</span>}
                      </td>
                      <td>
                        {(() => {
                          const suc = s.orden?.sucursal ?? s.sucursal;
                          return suc ? `${suc.determinante} — ${suc.nombre}` : '—';
                        })()}
                      </td>
                      <td>{fechaCorta(s.creadoEn)}</td>
                      <td className="numero">
                        {s.pendientes > 0
                          ? <span className="etiqueta no">{s.pendientes} de {s.partidas}</span>
                          : s.partidas}
                      </td>
                      <td><Etiqueta valor={s.estatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {abierta && (
        <FichaSolicitud
          id={abierta}
          onCerrar={() => setAbierta(null)}
          onCambio={cargar}
        />
      )}
    </>
  );
}

function FichaSolicitud({
  id, onCerrar, onCambio,
}: {
  id: string;
  onCerrar: () => void;
  onCambio: () => void;
}) {
  const { puede } = useSesion();
  const [solicitud, setSolicitud] = useState<SolicitudAlmacen | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  /** Cuánto surtir de cada partida y de qué bodega. */
  const [surtido, setSurtido] = useState<Record<string, { cantidad: string; bodegaId: string }>>({});
  const [escalando, setEscalando] = useState(false);
  /** La partida de texto libre que se está casando con el catálogo. */
  const [casando, setCasando] = useState<PartidaSolicitud | null>(null);
  const [justificacion, setJustificacion] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const cargar = useCallback(() => {
    api.get<SolicitudAlmacen>(`/solicitudes-almacen/${id}`)
      .then((s) => {
        setSolicitud(s);
        // Se propone surtir todo lo que se puede, de la bodega que más
        // tiene. Quien surte confirma o corrige; teclear lo obvio no es
        // trabajo de nadie.
        const propuesta: Record<string, { cantidad: string; bodegaId: string }> = {};
        for (const p of s.partidas) {
          if ((p.surtible ?? 0) > 0) {
            propuesta[p.id] = {
              cantidad: String(p.surtible),
              bodegaId: p.bodegas?.[0]?.id ?? '',
            };
          }
        }
        setSurtido(propuesta);
      })
      .catch(setError);
  }, [id]);

  useEffect(cargar, [cargar]);

  const lineas = Object.entries(surtido)
    .filter(([, v]) => Number(v.cantidad) > 0 && v.bodegaId)
    .map(([partidaId, v]) => ({
      partidaId, bodegaId: v.bodegaId, cantidad: Number(v.cantidad),
    }));

  // Lo que no hay y todavía nadie ha pedido. Una pieza ya escalada no
  // vuelve a la cuenta: pedirla dos veces es comprarla dos veces.
  const sinExistencia = (solicitud?.partidas ?? []).filter(
    (p) => (p.estatus === 'sin_stock' || (p.estatus === 'en_revision' && !p.refaccion))
      && !p.compra,
  );

  const yaPedidas = (solicitud?.partidas ?? []).filter((p) => p.compra);

  // Sólo mientras nada haya salido. Lo surtido —entero o a medias— ya
  // está cargado a la orden: cancelarlo no devolvería las piezas, sólo
  // borraría el rastro de por dónde salieron. Para eso está el ajuste.
  const puedeCancelarse = ['en_revision', 'disponible', 'sin_stock']
    .includes(solicitud?.estatus ?? '');

  async function surtir() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/solicitudes-almacen/${id}/surtir`, { lineas });
      cargar();
      onCambio();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  /**
   * Cancelar la que no se va a surtir.
   *
   * Faltaba: lo que no se podía surtir —pieza descatalogada, orden que se
   * cerró, pedido duplicado— se quedaba para siempre en la bandeja de
   * pendientes, que es la que almacén abre por omisión.
   */
  async function cancelar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/solicitudes-almacen/${id}/cancelar`, { motivo: motivo.trim() });
      setCancelando(false);
      setMotivo('');
      cargar();
      onCambio();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  async function escalar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/solicitudes-almacen/${id}/a-compras`, {
        partidaIds: sinExistencia.map((p) => p.id),
        justificacion: justificacion.trim(),
      });
      setEscalando(false);
      setJustificacion('');
      cargar();
      onCambio();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={solicitud ? `${solicitud.folio} · orden ${solicitud.orden?.folio}` : 'Solicitud'}
      onCerrar={onCerrar}
      pie={<button onClick={onCerrar}>Cerrar</button>}
    >
      <Error error={error} />

      {!solicitud ? <Cargando /> : (
        <>
          <p><Etiqueta valor={solicitud.estatus} /></p>

          {solicitud.notasParaAlmacen && (
            <p className="sub">{solicitud.notasParaAlmacen}</p>
          )}

          <table>
            <thead>
              <tr>
                <th>Pieza</th>
                {/* Con el que se cotiza y se pide al proveedor. Quien
                    surte lo busca ahí para confirmar que es la pieza. */}
                <th>Nº de parte</th>
                <th className="numero">Pedidas</th>
                <th className="numero">Surtidas</th>
                <th>Dónde está</th>
                <th>Surtir</th>
              </tr>
            </thead>
            <tbody>
              {solicitud.partidas.map((p) => (
                <Renglon
                  key={p.id}
                  partida={p}
                  valor={surtido[p.id]}
                  onCambiar={(v) => setSurtido((antes) => ({ ...antes, [p.id]: v }))}
                  onCasar={puede('almacen', 'editar') ? setCasando : undefined}
                />
              ))}
            </tbody>
          </table>

          {lineas.length > 0 && (
            <button
              className="primario"
              onClick={() => void surtir()}
              disabled={trabajando}
            >
              {trabajando ? 'Surtiendo…' : `Surtir ${lineas.reduce((s, l) => s + l.cantidad, 0)} piezas`}
            </button>
          )}

          {/* Lo que ya está pedido, antes que el botón de pedir: es la
              primera pregunta de quien vuelve a abrir la solicitud, y
              antes sólo se contestaba al intentar escalarla otra vez y
              chocar con el error. */}
          {yaPedidas.length > 0 && (
            <div className="aviso">
              <strong>Ya se pidió a compras.</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {yaPedidas.map((p) => (
                  <li key={p.id}>
                    {p.refaccion
                      ? <><span className="mono">{p.refaccion.codigo}</span> {p.refaccion.nombre}</>
                      : p.descripcionLibre}
                    {' · '}
                    <span className="mono">{p.compra?.folio}</span>
                    {' '}
                    <span className="etiqueta">{ETIQUETAS_COMPRA[p.compra?.estatus ?? ''] ?? p.compra?.estatus}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sinExistencia.length > 0 && !escalando && (
            <div className="campo">
              <button
                style={{ justifySelf: 'start' }}
                onClick={() => setEscalando(true)}
              >
                Pedir a compras lo que no hay ({sinExistencia.length})
              </button>
              <span className="ayuda">
                Antes de comprar: puede haberla en otra bodega, o servir una
                pieza equivalente.
              </span>
            </div>
          )}

          {/* Cancelar va al final y sin destacar: es la salida, no el
              camino. Sólo mientras nada haya salido del anaquel —lo
              surtido ya está cargado a la orden y cancelarlo sólo
              borraría el rastro—. */}
          {puedeCancelarse && !cancelando && !escalando && (
            <div className="campo">
              <button
                className="peligro" style={{ justifySelf: 'start' }}
                onClick={() => setCancelando(true)}
              >
                Cancelar la solicitud
              </button>
              <span className="ayuda">
                Para lo que ya no se va a surtir: la pieza se descatalogó,
                la orden se cerró, o se pidió dos veces.
              </span>
            </div>
          )}

          {cancelando && (
            <>
              <div className="campo">
                <label htmlFor="motivoCancelar">Por qué se cancela</label>
                <textarea
                  id="motivoCancelar" value={motivo} maxLength={500} rows={2}
                  onChange={(e) => setMotivo(e.target.value)}
                />
                <span className="ayuda">
                  Lo lee quien pidió la pieza. Sin explicación se queda
                  esperando algo que nadie va a traer.
                </span>
              </div>

              {motivo.trim().length < 5 && (
                <Faltantes que={['el motivo, de al menos 5 caracteres']} />
              )}

              <div className="controles">
                <button
                  className="peligro"
                  onClick={() => void cancelar()}
                  disabled={trabajando || motivo.trim().length < 5}
                >
                  {trabajando ? 'Cancelando…' : 'Cancelar la solicitud'}
                </button>
                <button onClick={() => setCancelando(false)}>Volver</button>
              </div>
            </>
          )}

          {casando && (
        <CasarConElCatalogo
          solicitudId={id}
          partida={casando}
          onCerrar={() => setCasando(null)}
          onHecho={() => { setCasando(null); cargar(); onCambio(); }}
        />
      )}

      {escalando && (
            <>
              <div className="campo">
                <label htmlFor="justificacion">Por qué hay que comprarla</label>
                <textarea
                  id="justificacion" value={justificacion} maxLength={1000} rows={2}
                  onChange={(e) => setJustificacion(e.target.value)}
                />
                <span className="ayuda">
                  Lo lee quien autoriza la compra, que no estuvo aquí.
                </span>
              </div>

              {justificacion.trim().length < 10 && (
                <Faltantes que={['la justificación, de al menos 10 caracteres']} />
              )}

              <div className="controles">
                <button
                  className="primario"
                  onClick={() => void escalar()}
                  disabled={trabajando || justificacion.trim().length < 10}
                >
                  {trabajando ? 'Enviando…' : 'Enviar a compras'}
                </button>
                <button onClick={() => setEscalando(false)}>Cancelar</button>
              </div>
            </>
          )}
        </>
      )}
    </Dialogo>
  );
}

/**
 * «Lo que pediste por su nombre es esta refacción del catálogo».
 *
 * La solicitud anticipada del preventivo admite texto libre a propósito:
 * se pide con treinta días de anticipación y la pieza puede no estar
 * dada de alta todavía. Lo que faltaba era la vuelta, y sin ella la
 * pieza llegaba y no entraba al inventario por su código.
 *
 * No borra lo que se escribió: la API lo guarda en las notas de la
 * partida. Si la refacción elegida resulta no ser la que querían, eso
 * es lo único que lo puede probar.
 */
function CasarConElCatalogo({ solicitudId, partida, onCerrar, onHecho }: {
  solicitudId: string;
  partida: PartidaSolicitud;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [refaccion, setRefaccion] = useState<Refaccion | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function guardar() {
    if (!refaccion) return;

    setError(null);
    setTrabajando(true);
    try {
      await api.post(
        `/solicitudes-almacen/${solicitudId}/partidas/${partida.id}/casar`,
        { refaccionId: refaccion.id },
      );
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo="Casar con el catálogo"
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario"
            onClick={guardar}
            disabled={!refaccion || trabajando}
          >
            {trabajando ? 'Guardando…' : 'Es ésta'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="ayuda">
        Se pidió como <strong>«{partida.descripcionLibre}»</strong>. Al
        casarla, la partida pasa a apuntar al catálogo y ya se puede surtir
        del anaquel. Lo que se escribió queda guardado en sus notas.
      </p>

      <BuscadorRefaccion
        id="casar-refaccion"
        valor={refaccion}
        onElegir={setRefaccion}
      />

      <Faltantes que={refaccion ? [] : ['elegir la refacción del catálogo']} />
    </Dialogo>
  );
}

function Renglon({
  partida, valor, onCambiar, onCasar,
}: {
  partida: PartidaSolicitud;
  valor?: { cantidad: string; bodegaId: string };
  onCambiar: (valor: { cantidad: string; bodegaId: string }) => void;
  /**
   * Casar lo pedido por su nombre con el catálogo.
   *
   * Opcional: sin permiso de editar en almacén no se ofrece, porque
   * sería un botón que siempre devuelve un 403.
   */
  onCasar?: (partida: PartidaSolicitud) => void;
}) {
  const bodegas = partida.bodegas ?? [];

  /**
   * Si lo elegido sale de una bodega que no es la de la plaza.
   *
   * La misma regla que la API —`bodega-sugerida.ts`— pero aquí se
   * recalcula sobre lo que el usuario acaba de escoger, que es cuando hay
   * que decírselo. Sólo si existe una de la plaza: si no, no hay desvío.
   */
  const elegida = valor?.bodegaId;
  const desviada = !!elegida
    && bodegas.some((b) => b.cercania === 'de_la_plaza')
    && bodegas.find((b) => b.id === elegida)?.cercania !== 'de_la_plaza';
  const surtible = partida.surtible ?? 0;

  return (
    <tr>
      <td>
        {partida.refaccion
          ? <>
              <span className="mono">{partida.refaccion.codigo}</span> — {partida.refaccion.nombre}
              {/* El plano de la pieza, a la mano de quien la va a sacar
                  del anaquel. Tenerlo sólo en la pantalla de catálogos
                  significa que nadie lo abre en el momento en que sirve. */}
              {partida.refaccion.tieneDespiece && (
                <div>
                  <button
                    className="enlace"
                    onClick={() => {
                      void api.descargar(
                        `/refacciones/${partida.refaccion!.id}/despiece`,
                        undefined,
                        `despiece-${partida.refaccion!.codigo}.pdf`,
                      );
                    }}
                  >
                    Ver despiece
                  </button>
                </div>
              )}
            </>
          : <>
              <span className="conteo">{partida.descripcionLibre}</span>
              <div className="ayuda">No está en el catálogo; hay que darla de alta para surtirla.</div>
              {/*
                Y el camino para hacerlo, que es lo que faltaba.

                El aviso decía «hay que darla de alta» y no ofrecía
                cómo: la pieza llegaba y no entraba al inventario por su
                código, porque se había pedido «manguera de succión» y
                el anaquel sabe de refacciones con folio. Esto sólo
                aparece mientras nadie la haya surtido: después,
                cambiarla diría que salió del anaquel algo distinto.
              */}
              {onCasar && partida.cantidadSurtida === 0 && (
                <div className="controles" style={{ marginTop: 6 }}>
                  <button className="enlace" onClick={() => onCasar(partida)}>
                    Es esta refacción del catálogo…
                  </button>
                </div>
              )}
            </>}
      </td>
      <td className="mono">{partida.refaccion?.numero_parte ?? '—'}</td>
      <td className="numero">{partida.cantidadSolicitada}</td>
      <td className="numero">{partida.cantidadSurtida}</td>
      <td>
        {/* «Sin existencia» y «sin existencia, ya pedida» son situaciones
            distintas para quien está esperando la pieza. */}
        {bodegas.length === 0
          ? partida.compra
            ? (
              <>
                <span className="etiqueta">Pedida a compras</span>
                <div className="ayuda mono">{partida.compra.folio}</div>
              </>
            )
            : <Etiqueta valor={partida.estatus} />
          : (
            /* Cada bodega con lo que tiene y de dónde es. La primera es
               la de la plaza de esta orden: se ofrece primero porque es
               de donde conviene sacarla, no porque tenga más. */
            <div style={{ display: 'grid', gap: 2 }}>
              {bodegas.map((b) => (
                <div key={b.id}>
                  {b.nombre}: {b.disponible}
                  {b.nota && <span className="ayuda"> · {b.nota}</span>}
                </div>
              ))}
            </div>
          )}
      </td>
      <td>
        {surtible === 0 ? '—' : (
          <div className="controles">
            <input
              type="number" min={0} max={surtible}
              style={{ width: 70 }}
              value={valor?.cantidad ?? ''}
              onChange={(e) => onCambiar({
                cantidad: e.target.value,
                bodegaId: valor?.bodegaId ?? bodegas[0]?.id ?? '',
              })}
            />
            {bodegas.length > 1 && (
              <select
                value={valor?.bodegaId ?? ''}
                onChange={(e) => onCambiar({
                  cantidad: valor?.cantidad ?? String(surtible),
                  bodegaId: e.target.value,
                })}
              >
                {/* En el mismo orden que las manda la API: la de la plaza,
                    el respaldo, y al final las de otra zona diciéndolo. */}
                {bodegas.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre}{b.nota ? ` — ${b.nota}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        {/*
          Sale de otra zona habiendo una de la plaza.

          Se avisa, no se impide: mandar de otra bodega pasa de verdad
          —el técnico va para allá, o la de la plaza no alcanza— y una
          regla que lo prohibiera se saltaría por fuera del sistema.
          Callado cuando no hay ninguna de la plaza: entonces no hay nada
          mejor que elegir y el aviso sería un reproche por algo que no se
          puede evitar.
        */}
        {desviada && (
          <div className="ayuda" style={{ marginTop: 4, color: 'var(--danger-fuerte)' }}>
            Sale de otra zona.
          </div>
        )}
      </td>
    </tr>
  );
}

export type { EstatusSolicitud };
